import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { destinationProblem } from '../../components/features/create/destination/createDestination';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { protocolImportsKey } from '../../lib/queryClient';
import { apiService } from '../../services/api.service';
import { ApiResponseError, unwrapApiResponse } from '../../services/api/http-client.types';
import { openBirdChat } from '../../services/events/birdChatEvents';
import { performResumableUpload } from '../../services/upload/performUpload';
import { safeAnalytics } from '../../utils/analytics';
import { getArtifactRoute } from '../../utils/artifactCapabilities';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../ui/useAppToast';

/**
 * Creating a structured protocol, from a document or from nothing.
 *
 * THE PROTOCOL IS CREATED BEFORE THE AGENT IS ASKED, and that ordering is the
 * whole design rather than an implementation detail. Handing a model a document
 * and leaving it to find somewhere to put the result meant it had nothing to
 * find: measured 2026-09-17, one real import spent 93 of its 107 tool calls
 * searching the workspace for a protocol nobody had created, and never once
 * called `propose_protocol`.
 *
 * So: upload the file, create the protocol linked to it, then give the agent
 * that id. There is a canonical target, nothing to search for, and a row the
 * user can watch from the moment they confirm.
 *
 * The three steps are deliberately NOT one call. Upload and artifact creation
 * are proven paths with their own quota, ACL and storage behaviour, and
 * re-implementing them behind a single multipart endpoint would fork both.
 * A failure between them leaves a visible, explicable protocol rather than a
 * silent nothing, which is the direction to fail in.
 *
 * It hands off to chat rather than spinning here. Bower is a panel column that
 * pushes page content across, so the import happens beside the library row it
 * is creating, and a refusal - "this document is not a protocol" - arrives as
 * an answer the user can read rather than a spinner that stopped.
 */

/** Where the protocol (and, for an import, its source document) is created. */
export interface StructuredProtocolParent {
  readonly parentEntityType: 'workspace' | 'project';
  readonly parentEntityId: string;
}

interface UseCreateStructuredProtocolOptions {
  /** Analytics label for the surface that opened this. */
  readonly surface: string;
  readonly parent: StructuredProtocolParent;
  /** The protocol exists: called before the hand-off navigates to it. */
  readonly onCreated: () => void;
  /**
   * The parent refused the create (trashed, or edit access lost). Given, it
   * replaces the error toast for those failures; every other failure toasts.
   */
  readonly onDestinationProblem?: (problem: 'gone' | 'forbidden') => void;
}

/**
 * What the specialist is asked to do.
 *
 * THE PROTOCOL TRAVELS AS A CONTEXT CHIP, not as an id spelled out in this
 * sentence. That is the difference between asking a model to parse a UUID out
 * of prose and handing it structured data: a chip lands in the session's
 * `ACTIVE_CONTEXT` block, sanitised, alongside an instruction the platform
 * already wrote for exactly this - "Use its ID directly with your tools - skip
 * any lookup/search step."
 *
 * Prose was not enough. Across four measured runs the search calls fell 93 ->
 * 27 -> 18 -> 12 as the wording got firmer, which is the shape of a lever that
 * does not hold rather than a fix. The chip is a different KIND of instruction.
 *
 * So the text says what to do and lets the chip say what to do it to. It still
 * does not describe the METHOD: the protocol-import skill owns that, and a
 * summary here would be a second copy of several hundred lines of guidance,
 * free to drift.
 */
const IMPORT_DIRECTIVE =
  'Structure the source document in my context into the protocol in my context - the protocol already exists and is waiting for its steps. ' +
  'Read the original file rather than any extracted text, and tell me what you could not map when you are done.';

/**
 * The resumable upload rejects with its own error class carrying the
 * backend's code (a trashed parent is `INVALID_PARENT` at reserve). Recast as
 * the shared API error so `destinationProblem` reads it; the message is kept.
 * Duck-typed so a test that stubs the upload module still works.
 */
function withApiCode(error: unknown): unknown {
  if (error instanceof ApiResponseError || !(error instanceof Error)) return error;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? new ApiResponseError(error.message, code) : error;
}

export function useCreateStructuredProtocol({
  surface,
  parent,
  onCreated,
  onDestinationProblem,
}: UseCreateStructuredProtocolOptions) {
  const { workspace } = useWorkspace();
  const toast = useAppToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isBusy, setIsBusy] = useState(false);

  const { parentEntityType, parentEntityId } = parent;

  const fail = useCallback(
    (title: string, error: unknown) => {
      const problem = onDestinationProblem ? destinationProblem(error) : null;
      if (problem && onDestinationProblem) {
        onDestinationProblem(problem);
        return;
      }
      toast({
        title,
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
      });
    },
    [toast, onDestinationProblem],
  );

  const requireWorkspace = useCallback((): string | null => {
    if (workspace?.id) return workspace.id;
    toast({
      title: 'Workspace required',
      description: 'Please select a workspace first.',
      status: 'error',
    });
    return null;
  }, [workspace, toast]);

  const startFromFile = useCallback(
    async (file: File) => {
      const workspaceId = requireWorkspace();
      if (!workspaceId) return;
      // The source document lives beside the protocol it becomes.
      const uploadParent = { parentEntityType, parentEntityId };

      setIsBusy(true);
      try {
        // 1. The source document. The protocol does not exist yet, so the link
        //    is recorded the other way round, on the protocol.
        //
        //    SOURCE, not import. The upload pipeline still extracts the text -
        //    the provenance check and the refusal guard both read it - but it
        //    files no note, because step 2 below creates the protocol. Left
        //    undeclared, this endpoint's default `import` also filed a note, so
        //    one document produced a protocol AND a near-duplicate note.
        let uploaded;
        try {
          uploaded = await performResumableUpload(file, {
            workspaceId,
            parentEntityType: uploadParent.parentEntityType,
            parentEntityId: uploadParent.parentEntityId,
            title: file.name,
            uploadIntent: 'source',
            resumeAmbiguousFinalizeOnce: true,
          });
        } catch (error) {
          throw withApiCode(error);
        }
        if (!uploaded.artifactId) {
          throw new Error('The document could not be uploaded');
        }
        const attachmentId = uploaded.artifactId;

        // 2. The protocol, linked to it and marked as being worked on. No
        //    client retry: a lost response would come back as a second protocol.
        const started = unwrapApiResponse(
          await apiService.request<{ protocolId: string; title?: string }>(
            {
              method: 'POST',
              url: '/protocols/import',
              data: { sourceAttachmentId: attachmentId, ...uploadParent },
            },
            0,
          ),
          'The protocol could not be created',
        );
        const protocolId = started?.protocolId;
        if (!protocolId) throw new Error('The protocol could not be created');

        safeAnalytics.setPersonProperty('last_protocol_created_at', new Date().toISOString());
        safeAnalytics.track(ANALYTICS_EVENTS.PROTOCOL_CREATED, {
          creation_method: surface,
          protocol_start: 'file',
        });

        // So the row and its "Importing" indicator appear immediately rather
        // than on the next poll. The user confirmed a second ago; a library
        // that looks unchanged reads as a click that did nothing.
        await queryClient.invalidateQueries({ queryKey: protocolImportsKey(workspaceId) });
        await queryClient.invalidateQueries({ queryKey: ['artifacts'] });

        // 3. The agent, given BOTH as context chips and NO raw file.
        //
        //    Passing `files` here uploaded the document a SECOND time: the
        //    composer path uploads whatever it is handed, so one import
        //    produced two identical attachments 0.85s apart, and the
        //    `imported_from` edge pointed at the one the agent never read.
        //
        //    The chip refers to the attachment already uploaded at step 1, so
        //    there is one file, one id, and the provenance edge names the file
        //    the model actually sees. The chip path inlines a PDF natively -
        //    its markdown is the pdfjs text layer, not a substitute for it.
        openBirdChat({
          source: surface,
          // A fresh thread: an import is a long, tool-heavy turn and dropping
          // it into whatever the user was last discussing buries both.
          freshContext: true,
          send: {
            text: IMPORT_DIRECTIVE,
            contexts: [
              {
                type: 'protocol',
                id: protocolId,
                // The placeholder the import wrote. A chip with no name renders
                // as a bare id to the user and reads as one to the model.
                name: started.title ?? file.name,
              },
              { type: 'attachment', id: attachmentId, name: file.name },
            ],
          },
        });
        onCreated();

        // LAND ON THE PROTOCOL, whichever door this was opened from. The
        // import status lives on the protocol itself. After `onCreated` and
        // after the chat handover, so the panel is already open beside the
        // page being navigated to.
        const route = getArtifactRoute('protocol', protocolId);
        if (route) navigate(route);
      } catch (error: unknown) {
        fail('Could not start the import', error);
      } finally {
        setIsBusy(false);
      }
    },
    [
      requireWorkspace,
      parentEntityType,
      parentEntityId,
      surface,
      queryClient,
      onCreated,
      navigate,
      fail,
    ],
  );

  /**
   * The from-scratch door: one call, no upload and no agent.
   *
   * It does NOT hand off to chat. The import does because a long model turn
   * needs somewhere to report from and something to refuse in; there is nothing
   * to report here, and opening a panel the user did not ask for would push the
   * page they are about to write in across the screen.
   */
  const startBlank = useCallback(async () => {
    const workspaceId = requireWorkspace();
    if (!workspaceId) return;

    setIsBusy(true);
    try {
      // No client retry, as for the import: a lost response would come back
      // as a second empty protocol.
      const created = unwrapApiResponse(
        await apiService.request<{ protocolId: string }>(
          {
            method: 'POST',
            url: '/protocols/blank',
            data: { parentEntityType, parentEntityId },
          },
          0,
        ),
        'The protocol could not be created',
      );
      const protocolId = created?.protocolId;
      if (!protocolId) throw new Error('The protocol could not be created');

      safeAnalytics.setPersonProperty('last_protocol_created_at', new Date().toISOString());
      safeAnalytics.track(ANALYTICS_EVENTS.PROTOCOL_CREATED, {
        creation_method: surface,
        protocol_start: 'blank',
      });

      // Both keys, same as the import path: the library row is an artifact and
      // the structured badge beside it is keyed separately, so refreshing one
      // leaves the other a poll behind.
      await queryClient.invalidateQueries({ queryKey: protocolImportsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ['artifacts'] });

      onCreated();
      const route = getArtifactRoute('protocol', protocolId);
      if (route) navigate(route);
    } catch (error: unknown) {
      fail('Could not create the protocol', error);
    } finally {
      setIsBusy(false);
    }
  }, [
    requireWorkspace,
    parentEntityType,
    parentEntityId,
    surface,
    queryClient,
    onCreated,
    navigate,
    fail,
  ]);

  return { startBlank, startFromFile, isBusy };
}
